import { useState } from "react";

import { ITenant, TenantRegion } from "../../../@types/admin/AdminHospital";
import {
  useAddTenant,
  useUpdateTenant,
} from "../../../hooks/mutations/admin/hospitals";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  AdminSelectField,
  FieldError,
  FieldHint,
  FilterButton,
  FilterGroup,
  FilterInput,
  FilterLabel,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
  ReadOnlyValue,
} from "../shared";

interface ITenantModalProps {
  /** Omit to create; pass a tenant to edit it. */
  tenant?: ITenant;
  onClose: () => void;
}

/** The API accepts only these two (`^(US|EU)$` on both request DTOs). */
const REGIONS: TenantRegion[] = ["EU", "US"];

/** AddTenantRequestDto/UpdateTenantRequestDto cap the name at 100. */
const MAX_NAME_LENGTH = 100;

function isTenantRegion(value: string): value is TenantRegion {
  return REGIONS.includes(value as TenantRegion);
}

/**
 * Add/edit tenant (EN-5153) — Retool's `AddTenantModal` and
 * `EditTenantModal`, which are the same two fields, so they are one component
 * here.
 *
 * ⚠️ Region is the tenant's data residency: it decides which regional
 * database a tenant's hospitals, staff and patients live in. Changing it on
 * an existing tenant does NOT move any data, so the edit form warns before
 * letting it through — Retool offered the same select with no comment.
 */
export function TenantModal({ tenant, onClose }: ITenantModalProps) {
  const { showToast } = useToast();
  const { addTenant, isAddingTenant } = useAddTenant();
  const { updateTenant, isUpdatingTenant } = useUpdateTenant();

  const isEdit = Boolean(tenant);
  const isSaving = isAddingTenant || isUpdatingTenant;

  const [name, setName] = useState(tenant?.name ?? "");
  const [region, setRegion] = useState<string>(tenant?.region ?? "EU");
  // A create form starts empty and invalid; only complain once touched.
  const [touched, setTouched] = useState(isEdit);

  const nameError = name.trim() ? undefined : "Name is required.";
  const regionError = isTenantRegion(region)
    ? undefined
    : "Region must be EU or US.";
  const hasErrors = Boolean(nameError || regionError);
  const regionChanged = isEdit && region !== tenant?.region;

  const handleSave = async () => {
    if (hasErrors || !isTenantRegion(region)) return;

    try {
      if (tenant) {
        await updateTenant({ id: tenant.id, name: name.trim(), region });
        showToast("The tenant was updated successfully.", "success");
      } else {
        await addTenant({ name: name.trim(), region });
        showToast("The tenant was created successfully.", "success");
      }
      onClose();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          isEdit
            ? "There was an error updating this tenant."
            : "There was an error creating this tenant."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>{isEdit ? "Edit tenant" : "Add tenant"}</ModalTitle>

      <ModalForm>
        {tenant && (
          <FilterGroup>
            <FilterLabel>ID</FilterLabel>
            <ReadOnlyValue>{tenant.id}</ReadOnlyValue>
          </FilterGroup>
        )}

        <FilterGroup>
          <FilterLabel htmlFor="tenant-name">Name</FilterLabel>
          <FilterInput
            id="tenant-name"
            type="text"
            maxLength={MAX_NAME_LENGTH}
            value={name}
            onBlur={() => setTouched(true)}
            onChange={(e) => {
              setTouched(true);
              setName(e.target.value);
            }}
          />
          {touched && nameError && <FieldError>{nameError}</FieldError>}
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="tenant-region">Region</FilterLabel>
          <AdminSelectField
            id="tenant-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
          >
            {REGIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </AdminSelectField>
          {regionChanged ? (
            <FieldError>
              Changing the region does not move existing data — the tenant
              &apos;s records stay in {tenant?.region}.
            </FieldError>
          ) : (
            <FieldHint>Which regional database this tenant lives in.</FieldHint>
          )}
        </FilterGroup>

        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSaving}
            onClick={onClose}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSaving || hasErrors}
            onClick={handleSave}
          >
            {isSaving ? "Saving..." : "Save"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
